import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { AuthenticatedRoutes } from '@/router/routes';

import { BillListItem } from '../../types';

interface BillingTableProps {
  table: Table<BillListItem>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  hasNonBillingPeriodFiltersApplied?: boolean;
  totalCount?: number;
  onRowClick: (invoice: BillListItem) => void;
  stickyHeader?: boolean;
  fixedHeight?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
}

export const BillingTable: React.FC<BillingTableProps> = ({
  hasNonBillingPeriodFiltersApplied,
  totalCount,
  onRowClick,
  stickyHeader = false,
  fixedHeight = false,
  tableId,
  onResizeEnd,
  ...paginatedTableProps
}) => {
  const { t } = useTranslation(['authenticated.bills', 'common']);
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // Billing period alone should still show the primary empty state with actions.
    if (hasNonBillingPeriodFiltersApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('table.noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }
    const isGenerateBillsEnabled = hasPermission(OpsiumPolicy.BillsGenerate);

    // If there are no invoices at all (total count is 0), show empty state with buttons
    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start px-8 text-left">
              <div className="flex flex-col">
                <div className="text-sm font-medium">{t('table.noBillsYet')}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('table.noBillsDescription')}
                </div>
                {isGenerateBillsEnabled && (
                  <div className="py-3">
                    <Button size="sm" onClick={() => navigate(AuthenticatedRoutes.GenerateBills)}>
                      <CirclePlus />
                      {t('generateBills')}
                    </Button>
                  </div>
                )}
              </div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      onRowClick={onRowClick}
      stickyHeader={stickyHeader}
      fixedHeight={fixedHeight}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
